import React from "react";
import { Check } from "lucide-react";

/**
 * GuardianPanel — issue #750 § 5.11. The under-11 guardian confirmation
 * panel on sign-up (#753): a navy-bordered card, prose, a rule line, and a
 * checkbox statement — stamped to `users.guardian_confirmed_at` by the
 * caller (#736), not by this presentational component.
 */
export function GuardianPanel({
  eyebrow,
  prose,
  ruleLine = null,
  checked = false,
  onCheckedChange,
  statement,
  className = "",
  "data-testid": testId,
  // Issue #753: the panel's own confirmation control needs a testid
  // distinct from the panel's — `register-guardian` names the container
  // (kept from the shipped `Register.jsx`), `register-guardian-confirm`
  // names the tick a journey/unit test clicks. No consumer needed this
  // before sign-up did, so it defaults to unset rather than forcing every
  // future caller to pass one.
  checkboxTestId,
  // #753 fix round F4: the DOM id a real `<label htmlFor>` points at.
  // Defaults to `checkboxTestId` — the shipped `Register.jsx` already uses
  // the same string for both `id` and `data-testid` on this exact control
  // (`id="register-guardian-confirm"`), so a caller that only ever needed
  // one identifier before still only has to pass one.
  checkboxId,
  ...rest
}) {
  const inputId = checkboxId || checkboxTestId || undefined;
  return (
    <div
      data-testid={testId}
      className={className}
      style={{
        background: "var(--nw-surface)",
        border: "var(--nw-border-emph)",
        borderRadius: "var(--nw-radius-card)",
        padding: "var(--nw-space-card)",
      }}
      {...rest}
    >
      {/* `SignUp.dc.html:100` draws this at 0.16em, an in-card section
          label (decision 2 of #750 § 4) — not the 0.18em page eyebrow. */}
      {eyebrow && (
        <div className="nw-section" style={{ color: "var(--nw-navy)", marginBottom: 8 }}>
          {eyebrow}
        </div>
      )}
      {prose && (
        <div className="nw-prose" style={{ color: "var(--nw-ink-lede)", marginBottom: 8 }}>
          {prose}
        </div>
      )}
      {ruleLine && (
        <div style={{ fontSize: 12, color: "var(--nw-ink-muted)", marginBottom: 12 }}>{ruleLine}</div>
      )}
      {/* #753 fix round F4: a REAL checkbox with a REAL label — the panel
          used to render a `<button aria-pressed>`, which assistive tech
          announces as "toggle button, pressed" where both the drawing and
          the issue say checkbox. This is the one attestation on the sign-up
          screen (an adult confirming they hold this account), the same
          native-control standard the shipped `Register.jsx` already met
          with a Radix `Checkbox` + `<label htmlFor>`. The visual (22px
          square, navy fill, checkmark) is unchanged — only the semantics
          move from a button pretending to be a checkbox to an actual one,
          via `appearance: none` on a real `<input type="checkbox">` rather
          than approximating the look on a non-form element. */}
      <label
        htmlFor={inputId}
        className="flex items-center"
        style={{ gap: 10, cursor: "pointer" }}
      >
        <span
          className="flex items-center justify-center"
          style={{ position: "relative", width: 22, height: 22, flexShrink: 0 }}
        >
          <input
            id={inputId}
            type="checkbox"
            data-testid={checkboxTestId}
            checked={checked}
            onChange={(e) => onCheckedChange?.(e.target.checked)}
            style={{
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              margin: 0,
              width: 22,
              height: 22,
              borderRadius: "var(--nw-radius-check)",
              border: checked ? "none" : "2px solid var(--nw-line-strong)",
              background: checked ? "var(--nw-navy)" : "var(--nw-surface)",
              cursor: "pointer",
            }}
          />
          {checked && (
            <Check
              aria-hidden="true"
              size={14}
              style={{ position: "absolute", top: 4, left: 4, color: "#FFFFFF", pointerEvents: "none" }}
            />
          )}
        </span>
        <span style={{ fontSize: 13, color: "var(--nw-ink)" }}>{statement}</span>
      </label>
    </div>
  );
}

export default GuardianPanel;
